import type { CSSProperties, ReactNode } from "react";

type IconProps = {
  size?: number;
  strokeWidth?: number;
  style?: CSSProperties;
};

function IconBase({
  size = 24,
  strokeWidth = 1.9,
  style,
  children
}: IconProps & { children: ReactNode }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
    >
      {children}
    </svg>
  );
}

export function HomeIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M3.5 10.5 12 4l8.5 6.5" />
      <path d="M5.5 9.5V20h5.5v-6.5h2V20h5.5V9.5" />
    </IconBase>
  );
}

export function CalendarIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <rect x="3.5" y="5.5" width="17" height="15" rx="3" />
      <path d="M7.5 3.5v4" />
      <path d="M16.5 3.5v4" />
      <path d="M3.5 10h17" />
    </IconBase>
  );
}

export function VideoIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <rect x="3.5" y="5.5" width="17" height="13" rx="3" />
      <path d="m10 9 5 3-5 3Z" fill="currentColor" stroke="none" />
    </IconBase>
  );
}

export function MessageIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M5 5.5h14a2 2 0 0 1 2 2v8.5a2 2 0 0 1-2 2H9l-5 3v-3.5a2 2 0 0 1-1-1.7V7.5a2 2 0 0 1 2-2Z" />
      <path d="M7.5 9.5h9" />
      <path d="M7.5 13h6" />
    </IconBase>
  );
}

export function ImageIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <rect x="3.5" y="5" width="17" height="14" rx="2.5" />
      <circle cx="8.2" cy="9.2" r="1.4" />
      <path d="m5.5 17 4.6-4.6 3.2 3.2 2-2 3.2 3.4" />
    </IconBase>
  );
}

export function BellIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M8 18.5h8" />
      <path d="M6.5 18.5h11l-1.5-2.5v-4.5a4 4 0 1 0-8 0V16Z" />
      <path d="M10 18.5a2 2 0 0 0 4 0" />
    </IconBase>
  );
}

export function HamburgerIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M4 6.5h16" />
      <path d="M4 12h16" />
      <path d="M4 17.5h16" />
    </IconBase>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="m6 6 12 12" />
      <path d="M18 6 6 18" />
    </IconBase>
  );
}

export function PersonIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5.5 19a6.5 6.5 0 0 1 13 0" />
    </IconBase>
  );
}

export function AdminIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M12 3.5 18.5 6v5.4c0 4-2.5 7.6-6.5 9.1-4-1.5-6.5-5.1-6.5-9.1V6Z" />
      <circle cx="12" cy="10" r="1.8" />
      <path d="M9.3 15a3.2 3.2 0 0 1 5.4 0" />
    </IconBase>
  );
}

export function TeacherIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <circle cx="8" cy="9" r="2.8" />
      <circle cx="16.5" cy="8" r="2.2" />
      <path d="M4.5 18a4 4 0 0 1 7 0" />
      <path d="M12.8 17.2a3.2 3.2 0 0 1 5.2 0" />
    </IconBase>
  );
}

export function StudentIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <circle cx="12" cy="8" r="3" />
      <path d="M6 19a6 6 0 0 1 12 0" />
    </IconBase>
  );
}

export function DocumentIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M7 3.5h7l4 4V20.5H7Z" />
      <path d="M14 3.5v4h4" />
      <path d="M9.5 11.5h5" />
      <path d="M9.5 15h5" />
    </IconBase>
  );
}

export function LockIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <rect x="5.5" y="10" width="13" height="10" rx="2.5" />
      <path d="M8.5 10V8a3.5 3.5 0 1 1 7 0v2" />
    </IconBase>
  );
}

export function SupportIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M4.5 12a7.5 7.5 0 0 1 15 0" />
      <path d="M5 13v3a2 2 0 0 0 2 2h1.5v-6H7a2 2 0 0 0-2 2Z" />
      <path d="M19 13v3a2 2 0 0 1-2 2h-1.5v-6H17a2 2 0 0 1 2 2Z" />
      <path d="M12 19.5h2.5" />
    </IconBase>
  );
}

export function ChevronRightIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="m9 5 6 7-6 7" />
    </IconBase>
  );
}

export function ClockIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.8v4.8l3.2 1.8" />
    </IconBase>
  );
}

export function LocationPinIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M12 20c3.2-3.6 5-6.4 5-9a5 5 0 1 0-10 0c0 2.6 1.8 5.4 5 9Z" />
      <circle cx="12" cy="11" r="1.9" />
    </IconBase>
  );
}

export function LinkIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M10 14 8 16a3 3 0 1 1-4.2-4.2l2-2" />
      <path d="m14 10 2-2a3 3 0 1 1 4.2 4.2l-2 2" />
      <path d="m9 15 6-6" />
    </IconBase>
  );
}

export function SettingsIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <circle cx="12" cy="12" r="2.6" />
      <path d="M12 3.8v2.1" />
      <path d="M12 18.1v2.1" />
      <path d="m5.6 5.6 1.5 1.5" />
      <path d="m16.9 16.9 1.5 1.5" />
      <path d="M3.8 12h2.1" />
      <path d="M18.1 12h2.1" />
      <path d="m5.6 18.4 1.5-1.5" />
      <path d="m16.9 7.1 1.5-1.5" />
    </IconBase>
  );
}

export function PlayCircleIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m10.2 8.8 5 3.2-5 3.2Z" fill="currentColor" stroke="none" />
    </IconBase>
  );
}
